import React, { useState, useEffect, useCallback } from 'react'
import {
  Collapse,
  Button,
  Tag,
  Input,
  Select,
  Space,
  Tooltip,
  Pagination,
  Modal,
  Empty,
  Form
} from 'antd'
import { _accept_delivery, _reject_delivery, _biker_pickup_order, _biker_deliver_order, _biker_start_delivery, _biker_arrived, _submit_biker_help_request } from '../../../common/axios_services'
import { showSuccessNotification, showErrorNotification } from '../../../common/utils/notification'
import useLocationTracking from '../../../hooks/useLocationTracking'
import {
  EyeOutlined,
  CarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  DollarOutlined
} from '@ant-design/icons'
import NumberFormat from 'react-number-format'
import { getDistanceWithFallback } from '../../../utils/distance'

const { Option } = Select

const TodayDeliveries = ({
  orders,
  loading,
  onViewOrder,
  onRefresh,
  updateFilters,
  pagination
}) => {
  const [filteredOrders, setFilteredOrders] = useState(orders)
  const [businessFilter, setBusinessFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [loadingStates, setLoadingStates] = useState({})
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [modalVisible, setModalVisible] = useState(false)
  const [pickupCode, setPickupCode] = useState('')
  const [collectionCode, setCollectionCode] = useState('')
  const [helpModalVisible, setHelpModalVisible] = useState(false)
  const [deliveryStep, setDeliveryStep] = useState('pickup')
  const [helpForm] = Form.useForm()
  const [helpSubmitting, setHelpSubmitting] = useState(false)
  const [helpNotes, setHelpNotes] = useState('')
  const { startTracking, stopTracking } = useLocationTracking(selectedOrder?.delivery?.id)

  React.useEffect(() => {
    let filtered = orders || []

    if (businessFilter) {
      filtered = filtered.filter(
        order => order.business?.name === businessFilter
      )
    }

    if (statusFilter) {
      filtered = filtered.filter(
        order => order.delivery?.deliveryStatus === statusFilter
      )
    }

    setFilteredOrders(filtered)
  }, [orders, businessFilter, statusFilter])

  const getStatusColor = status => {
    switch (status) {
      case 'ACCEPTED':
        return 'orange'
      case 'PICKED':
        return 'purple'
      case 'IN_TRANSIT':
        return 'blue'
      case 'ARRIVED':
        return 'cyan'
      case 'DELIVERED':
        return 'green'
      case 'REJECTED':
        return 'red'
      default:
        return 'default'
    }
  }

  const getStatusIcon = status => {
    switch (status) {
      case 'ACCEPTED':
        return <ClockCircleOutlined />
      case 'PICKED':
        return <EnvironmentOutlined />
      case 'IN_TRANSIT':
        return <CarOutlined />
      case 'ARRIVED':
        return <EnvironmentOutlined />
      case 'DELIVERED':
        return <CheckCircleOutlined />
      default:
        return <ClockCircleOutlined />
    }
  }

  const handleAccept = async (deliveryId) => {
    setLoadingStates(prev => ({ ...prev, [deliveryId]: { ...prev[deliveryId], accept: true } }))
    try {
      await _accept_delivery(deliveryId)
      showSuccessNotification('Delivery accepted successfully', 'Success')
      onRefresh()
    } catch (error) {
      showErrorNotification(error?.response?.data?.message || error?.message || 'Failed to accept delivery', 'Error')
    } finally {
      setLoadingStates(prev => ({ ...prev, [deliveryId]: { ...prev[deliveryId], accept: false } }))
    }
  }

  const handleReject = (deliveryId) => {
    Modal.confirm({
      title: 'Confirm Rejection',
      content: 'Note this order will be transferred to available delivery person',
      onOk: async () => {
        try {
          await _reject_delivery(deliveryId)
          showSuccessNotification('Delivery rejected successfully', 'Success')
          onRefresh()
        } catch (error) {
          showErrorNotification(error?.response?.data?.message || error?.message || 'Failed to reject delivery', 'Error')
        }
      }
    })
  }


  const handleArrived = () => {
    setDeliveryStep('deliver')
  }

  const handlePickOrder = async () => {
    if (!pickupCode) {
      showErrorNotification('Please enter pickup code', 'Error')
      return
    }
    try {
      await _biker_pickup_order(selectedOrder.delivery.id, pickupCode, '', '')
      showSuccessNotification('Order picked successfully', 'Success')
      setModalVisible(false)
      onRefresh()
    } catch (error) {
      showErrorNotification(error?.response?.data?.message || error?.message || 'Failed to pick order', 'Error')
    }
  }

  const handleStartTrip = async () => {
    try {
      await _biker_start_delivery(selectedOrder.delivery.id)
      showSuccessNotification('Trip started successfully', 'Success')
      startTracking()
      setModalVisible(false)
      onRefresh()
    } catch (error) {
      showErrorNotification(error?.response?.data?.message || error?.message || 'Failed to start trip', 'Error')
    }
  }

  const handleArrivedAction = async () => {
    try {
      await _biker_arrived(selectedOrder.delivery.id)
      showSuccessNotification('Arrived at location', 'Success')
      stopTracking()
      setModalVisible(false)
      onRefresh()
    } catch (error) {
      showErrorNotification(error?.response?.data?.message || error?.message || 'Failed to mark arrived', 'Error')
    }
  }

  const handleDeliver = async () => {
    if (!collectionCode) {
      showErrorNotification('Please enter collection code', 'Error')
      return
    }
    try {
      await _biker_deliver_order(selectedOrder.delivery.id, collectionCode)
      showSuccessNotification('Delivery completed successfully', 'Success')
      setDeliveryStep('delivered')
      setModalVisible(false)
      onRefresh()
    } catch (error) {
      showErrorNotification(error?.response?.data?.message || error?.message || 'Failed to complete delivery', 'Error')
    }
  }

  const handleHelpSubmit = async () => {
    try {
      setHelpSubmitting(true)

      let triggerType = 'BIKER_RUNNING_LATE'
      if (deliveryStep === 'deliver' || deliveryStep === 'arrived_help') {
        triggerType = 'BIKER_NOT_AVAILABLE'
      }

      await _submit_biker_help_request(selectedOrder.order.id, {
        orderId: selectedOrder.order.id,
        bikerId: selectedOrder.delivery.bikerId,
        triggerType: triggerType,
        notes: helpNotes
      })

      showSuccessNotification('Help request submitted. Support team has been notified.', 'Success')
      setHelpModalVisible(false)
      setHelpNotes('')
      onRefresh()
    } catch (error) {
      showErrorNotification(
        error?.response?.data?.message || error?.message || 'Failed to submit help request',
        'Error'
      )
    } finally {
      setHelpSubmitting(false)
    }
  }

  return (
    <div>
      {filteredOrders && filteredOrders.length > 0 ? (
        <Collapse>
          {filteredOrders.map((order, index) => (
            <Collapse.Panel
              key={order.order?.id}
              header={
                <div className='flex justify-between items-center w-full'>
                  <div className='flex items-center gap-4'>
                    <span className='font-medium'>
                      {order?.order?.orderCode} - {order?.business?.name}
                    </span>
                    <Tag
                      color={getStatusColor(order.delivery?.deliveryStatus)}
                      icon={getStatusIcon(order.delivery?.deliveryStatus)}
                    >
                      {order.delivery?.deliveryStatus}
                    </Tag>
                    <span className='text-sm text-gray-500'>
                      {new Date(order.order?.createdAt).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
              }
            >
              <div className='p-4 bg-gray-50'>
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
                  <div className='flex items-center gap-2'>
                    <ClockCircleOutlined className='text-gray-400' />
                    <div>
                      <div className='text-sm text-gray-500'>Time</div>
                      <div className='font-medium'>
                        {new Date(order.order?.createdAt).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <div>
                      <div className='text-sm text-gray-500'>Order ref</div>
                      <div className='font-medium'>{order?.order?.orderCode}</div>
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <div>
                      <div className='text-sm text-gray-500'>Business / Service Name</div>
                      <div className='font-medium'>{order?.business?.name}</div>
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <PhoneOutlined className='text-gray-400' />
                    <div>
                      <div className='text-sm text-gray-500'>Contact Telephone</div>
                      <div className='font-medium'>{order.business?.phone || 'N/A'}</div>
                    </div>
                  </div>
                  <div className='flex items-center gap-2 md:col-span-2'>
                    <EnvironmentOutlined className='text-gray-400' />
                    <div>
                      <div className='text-sm text-gray-500'>Pick-up Address</div>
                      <div className='font-medium'>{order.order?.pickupAddress || order.pickupAddress || order.business?.pickupAddressString || order.business?.address || 'N/A'}</div>
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <div>
                      <div className='text-sm text-gray-500'>Status</div>
                      <Tag
                        color={getStatusColor(order.delivery?.deliveryStatus)}
                        icon={getStatusIcon(order.delivery?.deliveryStatus)}
                      >
                        {order.delivery?.deliveryStatus}
                      </Tag>
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <EnvironmentOutlined className='text-gray-400' />
                    <div className="flex items-center gap-3">
                      <div>
                        <div className='text-sm text-gray-500'>Distance</div>
                        <div className='font-medium'>
                          {getDistanceWithFallback(order.delivery?.trackerLink, order.delivery?.distance)} km
                        </div>
                      </div>
                      <Button
                        size="small"
                        type="primary"
                        className="bg-green-600 hover:bg-green-700 border-none font-bold px-4"
                        onClick={() => window.open(order.delivery.trackerLink, '_blank')}
                      >
                        VIEW MAP
                      </Button>
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <DollarOutlined className='text-gray-400' />
                    <div>
                      <div className='text-sm text-gray-500'>Del fee</div>
                      <div className='font-medium'>
                        <NumberFormat
                          thousandSeparator={true}
                          prefix='₦'
                          displayType='text'
                          value={order.delivery?.deliveryFee || 0}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className='flex gap-2 mt-4'>
                  {order.delivery?.deliveryStatus === 'WAITING' && (
                    <>
                      <Button
                        type='primary'
                        loading={loadingStates[order.delivery.id]?.accept}
                        onClick={() => handleAccept(order.delivery.id)}
                      >
                        Accept
                      </Button>
                      <Button
                        danger
                        onClick={() => handleReject(order.delivery.id)}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  {(order.delivery?.deliveryStatus === 'ACCEPTED' || order.delivery?.deliveryStatus === 'PICKED' || order.delivery?.deliveryStatus === 'DELIVERED') && (
                    <Button
                      type='primary'
                      onClick={() => {
                        setSelectedOrder(order)
                        setDeliveryStep(order.delivery?.deliveryStatus === 'ACCEPTED' ? 'pickup' : order.delivery?.deliveryStatus === 'PICKED' ? 'start_trip' : order.delivery?.deliveryStatus === 'IN_TRANSIT' ? 'arrived_action' : order.delivery?.deliveryStatus === 'ARRIVED' ? 'arrived_help' : 'delivered')
                        setModalVisible(true)
                      }}
                    >
                      View
                    </Button>
                  )}
                </div>
              </div>
            </Collapse.Panel>
          ))}
        </Collapse>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No active deliveries found"
        />
      )}

      <Pagination
        current={pagination?.page || 0}
        pageSize={pagination?.limit || 0}
        total={pagination?.total || 0}
        onChange={(page, pageSize) => updateFilters({ page, limit: pageSize })}
        showSizeChanger
        showQuickJumper
        showTotal={(total, range) =>
          `${range[0]}-${range[1]} of ${total} deliveries`
        }
        style={{ marginTop: 16, textAlign: 'center' }}
      />

      <Modal
        title="Order Details"
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedOrder && (
          <div className='space-y-4'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <strong>Pick-up Time:</strong> {new Date(selectedOrder.order?.createdAt).toLocaleString()}
              </div>
              <div>
                <strong>Order Ref Code:</strong> {selectedOrder.order?.orderCode}
              </div>
              <div>
                <strong>Business Name/Phone:</strong> {selectedOrder.business?.name} / {selectedOrder.business?.phoneCode}{selectedOrder.business?.phone}
              </div>
              <div>
                <strong>Buyer Name/Phone:</strong> {selectedOrder.customer?.name} / {selectedOrder.customer?.phoneCode}{selectedOrder.customer?.phone}
              </div>
              <div className='md:col-span-2'>
                <strong>Delivery Address:</strong> {selectedOrder.customer?.address || 'N/A'}
              </div>
            </div>
            <div>
              <strong>List of Items:</strong>
              <div className='mt-2 space-y-2'>
                {selectedOrder?.items?.map((item, index) => (
                  <div key={item.id} className='flex justify-between items-center p-2 bg-gray-100 rounded'>
                    <div>
                      <div className='font-medium'>{item.menuItem?.name}</div>
                      <div className='text-sm text-gray-600'>Qty: {item.quantity} | {item.deliveryDateType}</div>
                    </div>
                    <div className='text-right'>
                      <div className='font-medium'>
                        <NumberFormat
                          thousandSeparator={true}
                          prefix='₦'
                          displayType='text'
                          value={item.price}
                        />
                      </div>
                      <div className='text-sm text-gray-600'>
                        <NumberFormat
                          thousandSeparator={true}
                          prefix='₦'
                          displayType='text'
                          value={item.menuItem?.price}
                        /> each
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className='flex gap-2'>
              {deliveryStep === 'pickup' && (
                <>
                  <Input
                    type="number"
                    inputMode="numeric"
                    placeholder="Enter pickup code"
                    value={pickupCode}
                    onChange={(e) => setPickupCode(e.target.value)}
                    style={{ width: 200, marginRight: 8 }}
                  />
                  <Button type='primary' onClick={handlePickOrder}>PICK ORDER</Button>
                </>
              )}
              {deliveryStep === 'start_trip' && (
                <Button type='primary' onClick={handleStartTrip}>START TRIP</Button>
              )}
              {deliveryStep === 'arrived_action' && (
                <Button type='primary' onClick={handleArrivedAction}>ARRIVED</Button>
              )}
              {deliveryStep === 'arrived_help' && (
                <Button onClick={() => setHelpModalVisible(true)}>Help</Button>
              )}
              {deliveryStep === 'arrived' && (
                <>
                  <Button onClick={handleArrived}>Arrived</Button>
                  <Button onClick={() => setHelpModalVisible(true)}>Help</Button>
                </>
              )}
              {deliveryStep === 'deliver' && (
                <>
                  <Input
                    placeholder="Enter collection code"
                    value={collectionCode}
                    onChange={(e) => setCollectionCode(e.target.value)}
                    style={{ width: 200, marginRight: 8 }}
                  />
                  <Button type='primary' onClick={handleDeliver}>Deliver</Button>
                  <Button onClick={() => setHelpModalVisible(true)}>Help</Button>
                </>
              )}
              {deliveryStep === 'delivered' && (
                <div>Delivery completed</div>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="Request Help"
        open={helpModalVisible}
        onCancel={() => {
          setHelpModalVisible(false)
          setHelpNotes('')
        }}
        onOk={handleHelpSubmit}
        confirmLoading={helpSubmitting}
        width={500}
      >
        <Form
          layout='vertical'
          autoComplete='off'
        >
          <Form.Item
            label='What is the issue?'
          >
            <p style={{ fontWeight: 500, marginBottom: 10 }}>
              {deliveryStep === 'arrived_action' ? 'Delivery Running Late' : 'Unable to Complete Delivery'}
            </p>
          </Form.Item>
          <Form.Item
            label='Additional notes (optional)'
          >
            <Input.TextArea
              rows={4}
              placeholder='Tell support team more about the issue'
              value={helpNotes}
              onChange={(e) => setHelpNotes(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default TodayDeliveries
